import { EntityFilters, PageFilterState } from '@/app/lib/page-filters/types';
import { DateFilter, Filter } from '@/legacy/types';
import { createInitialDateFilter } from '@/legacy/utils/initialFilters';
import { CUSTOMIZED_FILTER_KEY } from '@/legacy/constants';

export type { PageFilterState };

type SetFunction = (
	partial: Partial<PageState> | ((state: PageState) => Partial<PageState>),
) => void;

export type PageState = {
	filters: Record<string, PageFilterState>;

	setDateFilter: (pageKey: string, filter: DateFilter) => void;

	setEntityFilter: <K extends keyof EntityFilters>(
		pageKey: string,
		filterKey: K,
		value: EntityFilters[K],
	) => void;
	setEntityFilters: (pageKey: string, values: EntityFilters) => void;
	clearEntityFilters: (pageKey: string) => void;

	setFilters: (filters: Record<string, PageFilterState>) => void;

	sessionFilters: Record<string, Filter>;
	toggleSessionFilter: (pageKey: string, k: string, v: string | number | boolean) => void;

	filtersDisabledByKey: Record<string, boolean>;
	setFiltersDisabledByKey: (pageKey: string, value: boolean) => void;

	resetPageSlice: () => void;

	ensurePage: (pageKey: string, defaults?: Partial<PageFilterState>) => void;
};

const initialFiltersState: Pick<PageState, 'filters'> = {
	filters: {},
};

const createDefaultPageFilterState = (): PageFilterState => ({
	date: createInitialDateFilter(),
});

const createPageSlice = (
	set: SetFunction,
	setSelectedDateForGraph: (filter: DateFilter) => void,
): PageState => ({
	...initialFiltersState,

	setDateFilter: (pageKey, filter) => {
		// For non-custom presets store only the label — dates are computed at query time.
		// For custom ranges store the full filter (the dates are the user's explicit intent).
		const stored: DateFilter =
			filter.label === CUSTOMIZED_FILTER_KEY ? filter : { label: filter.label };

		set((state) => {
			const prev = state.filters[pageKey] ?? createDefaultPageFilterState();
			return {
				...state,
				filters: {
					...state.filters,
					[pageKey]: { ...prev, date: stored },
				},
			};
		});

		setSelectedDateForGraph(filter);
	},

	setEntityFilter: (pageKey, filterKey, value) =>
		set((state) => {
			const prev = state.filters[pageKey] ?? createDefaultPageFilterState();

			return {
				...state,
				filters: {
					...state.filters,
					[pageKey]: {
						...prev,
						entities: {
							...prev.entities,
							[filterKey]: value,
						},
					},
				},
			};
		}),

	setEntityFilters: (pageKey, values) =>
		set((state) => {
			const prev = state.filters[pageKey] ?? createDefaultPageFilterState();

			return {
				...state,
				filters: {
					...state.filters,
					[pageKey]: {
						...prev,
						entities: { ...prev.entities, ...values },
					},
				},
			};
		}),

	clearEntityFilters: (pageKey) =>
		set((state) => {
			const prev = state.filters[pageKey] ?? createDefaultPageFilterState();
			return {
				...state,
				filters: {
					...state.filters,
					[pageKey]: {
						...prev,
						entities: {},
					},
				},
			};
		}),

	setFilters: (filters) =>
		set((state) => ({
			...state,
			filters: { ...state.filters, ...filters },
		})),

	sessionFilters: {},

	toggleSessionFilter: (pageKey, k, v) =>
		set((s) => {
			const next = { ...(s.sessionFilters[pageKey] ?? {}) };
			if (next[k] === v) delete next[k];
			else next[k] = v;
			return { ...s, sessionFilters: { ...s.sessionFilters, [pageKey]: next } };
		}),

	filtersDisabledByKey: {},
	setFiltersDisabledByKey: (pageKey, value) =>
		set((state) => ({
			...state,
			filtersDisabledByKey: { ...state.filtersDisabledByKey, [pageKey]: value },
		})),

	ensurePage: (pageKey: string, defaults?: Partial<PageFilterState>) =>
		set((state) => {
			if (state.filters[pageKey]) return state;
			return {
				...state,
				filters: {
					...state.filters,
					[pageKey]: { ...createDefaultPageFilterState(), ...defaults },
				},
			};
		}),

	resetPageSlice: () => set(() => initialFiltersState),
});

export { createPageSlice };
